import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import { ArrowRight, Key, Sparkles, HeartHandshake } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1678889284769-b7dcbec1f082?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Home() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/properties")
      .then((r) => setProperties(r.data))
      .finally(() => setLoading(false));
  }, []);

  const featured = properties.filter((p) => p.featured).slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Luxury villa at twilight"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/70 to-[#121212]/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#121212]/70 via-transparent to-transparent" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 md:pb-32 pt-40 w-full">
          <p className="overline text-[#D4AF37] mb-6" data-testid="hero-overline">
            A private collection · Est. 2024
          </p>
          <h1
            className="font-serif-luxe text-6xl sm:text-7xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tighter max-w-5xl"
            data-testid="hero-title"
          >
            The quiet luxury
            <br />
            of <span className="italic text-[#D4AF37]">arriving home</span>
            <br />
            somewhere new.
          </h1>
          <p className="mt-8 max-w-xl text-lg text-[#F5F5DC]/80 leading-relaxed">
            A hand-picked collection of vacation homes, personally hosted. No
            middleman. No lockboxes. Just a warm welcome and the keys in your
            hand.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/properties"
              data-testid="hero-view-properties-btn"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-[#D4AF37] text-[#121212] text-xs tracking-[0.24em] uppercase hover:bg-[#B3922E] transition-all hover:translate-y-[-2px]"
            >
              View Properties
              <ArrowRight
                size={16}
                className="group-hover:translate-x-1 transition-transform"
              />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-btn"
              className="inline-flex items-center gap-3 px-8 py-4 border border-[#F5F5DC]/20 hover:border-[#D4AF37] text-xs tracking-[0.24em] uppercase transition-all"
            >
              Talk to the host
            </Link>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="overline mb-4">Our philosophy</p>
            <h2 className="font-serif-luxe text-5xl md:text-6xl leading-none">
              Small in scale.
              <br />
              <span className="italic text-[#D4AF37]">Considered</span> in every
              detail.
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-8">
            <p className="text-lg text-[#F5F5DC]/80 leading-relaxed">
              Every home in our collection has been walked through, slept in,
              and chosen personally — because a great stay is more than a
              listing. It's linen that feels like something, a kitchen that
              works before your first coffee, and a host who answers on the
              first ring.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 pt-6">
              <FeatureBlock icon={<Key size={20} />} title="Direct with the host" desc="Book with the owner. No commission fees, no ghost concierge." />
              <FeatureBlock icon={<Sparkles size={20} />} title="Editorial standard" desc="Every home meets a boutique-hotel bar for design and comfort." />
              <FeatureBlock icon={<HeartHandshake size={20} />} title="Personally hosted" desc="From arrival to farewell, we're the ones you talk to." />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PROPERTIES */}
      <section
        data-testid="featured-section"
        className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16"
      >
        <div className="flex items-end justify-between mb-16">
          <div>
            <p className="overline mb-4">The Collection</p>
            <h2 className="font-serif-luxe text-5xl md:text-6xl leading-none">
              Featured stays
            </h2>
          </div>
          <Link
            to="/properties"
            data-testid="featured-view-all"
            className="hidden md:inline-flex items-center gap-2 link-underline text-sm tracking-wide uppercase"
          >
            View all <ArrowRight size={14} />
          </Link>
        </div>

        {loading ? (
          <p className="text-[#A3A3A3]">Loading the collection…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(featured.length ? featured : properties.slice(0, 3)).map((p, i) => (
              <PropertyCard property={p} index={i} key={p.id} />
            ))}
          </div>
        )}
      </section>

      {/* CTA STRIP */}
      <section className="mt-24 border-y border-[#F5F5DC]/10 bg-[#1C1C1E]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="font-serif-luxe text-4xl md:text-5xl leading-none max-w-xl">
            Have a stay in mind? Let's <span className="italic text-[#D4AF37]">talk directly</span>.
          </h2>
          <Link
            to="/contact"
            data-testid="home-cta-contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#D4AF37] text-[#121212] text-xs tracking-[0.24em] uppercase hover:bg-[#B3922E] transition-all hover:translate-y-[-2px] self-start md:self-auto"
          >
            Start the conversation
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function FeatureBlock({ icon, title, desc }) {
  return (
    <div className="space-y-3">
      <div className="w-10 h-10 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37]">
        {icon}
      </div>
      <h4 className="font-serif-luxe text-xl">{title}</h4>
      <p className="text-sm text-[#A3A3A3] leading-relaxed">{desc}</p>
    </div>
  );
}
